import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, RotateCcw, ShieldCheck } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import Login from '@/pages/Login';
import { createNfcIdempotencyKey, submitNfcAttendance } from '@/lib/nfcAttendance';

const PENDING_NFC_KEY = 'restaurantos:nfc-simple:pending';

function clearPendingNfc() {
  window.sessionStorage.removeItem(PENDING_NFC_KEY);
}

function preparePendingNfc(token) {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(PENDING_NFC_KEY) || 'null');
    if (
      stored?.token === token
      && typeof stored?.idempotencyKey === 'string'
      && stored.idempotencyKey.length >= 16
    ) {
      return stored;
    }
  } catch (_) {
    clearPendingNfc();
  }

  const pending = {
    token,
    idempotencyKey: createNfcIdempotencyKey(),
  };
  window.sessionStorage.setItem(PENDING_NFC_KEY, JSON.stringify(pending));
  return pending;
}

function formatServerTime(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('it-IT', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
}

function formatHours(seconds) {
  if (seconds == null) return null;
  const totalMinutes = Math.round(Number(seconds) / 60);
  return `${Math.floor(totalMinutes / 60)}h ${String(totalMinutes % 60).padStart(2, '0')}m`;
}

function errorMessage(error) {
  const detail = String(error?.body?.detail || error?.message || '').toLowerCase();
  if (error?.status === 401) return 'Accedi nuovamente per registrare la presenza.';
  if (error?.status === 403) {
    if (detail.includes('token nfc')) return 'Tag NFC non valido.';
    if (detail.includes('punto nfc')) return 'Tag NFC non attivo.';
    return 'Non sei autorizzato a registrare la presenza in questo locale.';
  }
  if (error?.status === 409) return 'La richiesta è già stata gestita. Riprova senza avvicinare nuovamente il tag.';
  if (error?.status === 422 || error?.status === 400) return 'Tag NFC non valido.';
  return 'Non è stato possibile registrare la presenza. Riprova.';
}

function Shell({ children, testId }) {
  return (
    <div className="min-h-screen bg-app-background flex items-center justify-center px-5 py-10" data-testid={testId}>
      <div className="w-full max-w-md rounded-2xl border border-stone-200 bg-white p-7 shadow-sm sm:p-9">
        <div className="mb-6 flex justify-center">
          <img
            src="/brand/restaurantos-logo-horizontal.svg"
            alt="RestaurantOS"
            className="h-12 w-auto sm:h-14"
          />
        </div>
        {children}
      </div>
    </div>
  );
}

export default function NfcBrowserAttendance() {
  const { token: routeToken = '' } = useParams();
  const routeLocation = useLocation();
  const { isAuthenticated, loading, me, signOut } = useAuth();
  const navigate = useNavigate();
  const submittingRef = useRef(false);
  const [pending, setPending] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    clearPendingNfc();
    setPending(null);
    setInvalid(true);
  }, []);

  const submit = useCallback(async () => {
    if (!pending || submittingRef.current) return;
    submittingRef.current = true;
    setError(null);
    try {
      const result = await submitNfcAttendance(
        pending.token,
        pending.idempotencyKey,
      );
      clearPendingNfc();
      navigate('/nfc/esito', {
        replace: true,
        state: { result },
      });
    } catch (submitError) {
      setError(submitError);
      if (submitError?.status === 401) {
        try {
          await signOut();
        } catch (_) {
          // Keep the pending request available for an explicit retry.
        }
      }
    } finally {
      submittingRef.current = false;
    }
  }, [navigate, pending, signOut]);

  useEffect(() => {
    if (!loading && isAuthenticated && me && pending && !invalid) {
      submit();
    }
  }, [invalid, isAuthenticated, loading, me, pending, submit]);

  if (loading || (!invalid && !pending)) {
    return (
      <Shell testId="nfc-browser-loading">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-brand-green" />
          <h1 className="mt-4 font-display text-2xl font-bold text-brand-navy">Preparazione NFC…</h1>
        </div>
      </Shell>
    );
  }

  if (invalid) {
    return (
      <Shell testId="nfc-browser-invalid">
        <AlertCircle className="mx-auto h-10 w-10 text-red-600" />
        <h1 className="mt-4 text-center font-display text-2xl font-bold text-brand-navy">Link NFC legacy disabilitato</h1>
        <p className="mt-2 text-center text-sm text-stone-500">Per registrare la presenza, accedi all’app e scansiona il tag SUN dal Dashboard Dipendente.</p>
      </Shell>
    );
  }

  if (!isAuthenticated) {
    return <Login returnTo={`/nfc/${routeToken}`} nfcMode />;
  }

  return (
    <Shell testId="nfc-browser-submitting">
      {error ? (
        <div className="text-center">
          <AlertCircle className="mx-auto h-10 w-10 text-red-600" />
          <h1 className="mt-4 font-display text-2xl font-bold text-brand-navy">{errorMessage(error)}</h1>
          <p className="mt-2 text-sm text-stone-500">Il retry usa la stessa richiesta e non crea duplicati.</p>
          <Button className="mt-6 w-full gap-2 bg-brand-green text-white hover:bg-brand-green-hover" onClick={submit}>
            <RotateCcw className="h-4 w-4" /> Riprova
          </Button>
        </div>
      ) : (
        <div className="text-center">
          <Loader2 className="mx-auto h-9 w-9 animate-spin text-brand-green" />
          <h1 className="mt-4 font-display text-2xl font-bold text-brand-navy">Registrazione presenza in corso…</h1>
          <p className="mt-2 text-sm text-stone-500">Identità, locale e orario vengono verificati dal server.</p>
        </div>
      )}
    </Shell>
  );
}

export function NfcAttendanceOutcome() {
  const location = useLocation();
  const result = location.state?.result;
  const eventType = result?.event?.event_type;
  const reasonCode = result?.event?.reason_code || '';
  const rejected = result?.outcome === 'DA_VALIDARE';
  const unauthorized = rejected && (
    reasonCode.startsWith('AUTHORIZATION_')
    || reasonCode === 'EMPLOYEE_LOCATION_NOT_AUTHORIZED'
  );
  const record = result?.record;
  const title = unauthorized
    ? 'Non sei autorizzato a registrare la presenza in questo locale.'
    : rejected
      ? 'Presenza non registrata'
      : result?.idempotent
    ? 'Presenza già registrata'
    : eventType === 'EXIT'
      ? 'Uscita registrata'
      : eventType === 'ENTRY'
        ? 'Entrata registrata'
        : 'Registrazione completata';
  const occurredAt = formatServerTime(
    eventType === 'EXIT'
      ? record?.exited_at_utc || result?.event?.occurred_at
      : record?.entered_at_utc || result?.event?.occurred_at,
  );
  const hours = eventType === 'EXIT' ? formatHours(record?.hours_seconds) : null;

  return (
    <Shell testId="nfc-browser-outcome">
      {rejected
        ? <AlertCircle className="mx-auto h-11 w-11 text-red-600" />
        : <CheckCircle2 className="mx-auto h-11 w-11 text-brand-green" />}
      <h1 className="mt-4 text-center font-display text-3xl font-bold text-brand-navy">{title}</h1>
      {!rejected && occurredAt && <p className="mt-3 text-center text-lg font-semibold text-brand-navy">Ora: {occurredAt}</p>}
      {!rejected && hours && <p className="mt-1 text-center text-sm text-stone-500">Ore della presenza: {hours}</p>}
      <div className="mt-6 flex items-center justify-center gap-2 text-xs text-stone-500">
        <ShieldCheck className="h-4 w-4 text-brand-green" />
        Orario e azione determinati dal server
      </div>
    </Shell>
  );
}